import React, { useState } from "react";
import { clientService, Client, UpdateClientDto, ClientLanguage, validateOrigin } from "../services/client.service";
import { useToast } from "../context/ToastContext";
import "../styles/clients.css";

interface Props {
    client: Client;
    onClose: () => void;
    onUpdated: () => void;
}

export default function EditClientModal({ client, onClose, onUpdated }: Props) {
    const { showToast } = useToast();
    const [name, setName] = useState(client.name);
    const [displayName, setDisplayName] = useState(client.displayName || "");
    const [layoutColor, setLayoutColor] = useState(client.layoutColor || "#4f46e5");
    const [type, setType] = useState(client.type || "");
    const [language, setLanguage] = useState<ClientLanguage | "">(client.language || "");
    const [allowedOrigins, setAllowedOrigins] = useState(
        (client.allowedOrigins || []).join("\n")
    );
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            const payload: UpdateClientDto = {};
            if (name.trim() !== client.name) payload.name = name.trim();
            if (displayName.trim() !== (client.displayName || "")) payload.displayName = displayName.trim();
            if (layoutColor.trim() !== (client.layoutColor || "")) payload.layoutColor = layoutColor.trim();
            if (type.trim() !== (client.type || "")) payload.type = type.trim() || undefined;
            if (language !== (client.language || "")) payload.language = language || undefined;
            const newOrigins = allowedOrigins
                .split("\n")
                .map((s) => s.trim())
                .filter(Boolean);
            for (const o of newOrigins) {
                const err = validateOrigin(o);
                if (err) { setError(err); setLoading(false); return; }
            }
            const oldOrigins = client.allowedOrigins || [];
            if (JSON.stringify(newOrigins) !== JSON.stringify(oldOrigins)) {
                payload.allowedOrigins = newOrigins;
            }

            if (Object.keys(payload).length === 0) {
                onClose();
                return;
            }

            await clientService.update(client.id, payload);
            showToast("Client updated successfully.");
            onUpdated();
        } catch (err: any) {
            setError(err.response?.data?.msg || "Failed to update client.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                <h2>Edit Client</h2>
                <p className="modal-subtitle">Update client details</p>

                <form className="modal-form" onSubmit={handleSubmit}>
                    {error && <div className="modal-error">{error}</div>}

                    <div className="modal-field">
                        <label htmlFor="edit-name">Name</label>
                        <input
                            id="edit-name"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            autoFocus
                        />
                    </div>

                    <div className="modal-field">
                        <label htmlFor="edit-display-name">Display Name</label>
                        <input
                            id="edit-display-name"
                            type="text"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            placeholder="Shown on the chat widget (optional)"
                        />
                        <span className="modal-field-hint">If empty, the default "RMT-AI" will be displayed.</span>
                    </div>

                    <div className="modal-field">
                        <label htmlFor="edit-layout-color">Layout Color</label>
                        <input
                            id="edit-layout-color"
                            type="color"
                            className="color-picker"
                            value={layoutColor}
                            onChange={(e) => setLayoutColor(e.target.value)}
                        />
                        <span className="modal-field-hint">Theme color for the chat widget.</span>
                    </div>

                    <div className="modal-field">
                        <label htmlFor="edit-type">Type</label>
                        <select
                            id="edit-type"
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                        >
                            <option value="">No type</option>
                            <option value="enterprise">Enterprise</option>
                            <option value="startup">Startup</option>
                            <option value="personal">Personal</option>
                        </select>
                    </div>

                    <div className="modal-field">
                        <label htmlFor="edit-language">Language</label>
                        <select
                            id="edit-language"
                            value={language}
                            onChange={(e) => setLanguage(e.target.value as ClientLanguage | "")}
                        >
                            <option value="">No language</option>
                            <option value="fr">French</option>
                            <option value="en">English</option>
                            <option value="lb">Luxembourgish</option>
                        </select>
                    </div>

                    <div className="modal-field">
                        <label htmlFor="edit-origins">Allowed Origins</label>
                        <textarea
                            id="edit-origins"
                            value={allowedOrigins}
                            onChange={(e) => setAllowedOrigins(e.target.value)}
                            placeholder={"https://example.com\nhttps://app.example.com"}
                            rows={3}
                        />
                        <span className="modal-field-hint">One origin per line. Leave empty to allow all origins. Local origins (localhost, 127.0.0.1) are not allowed.</span>
                    </div>

                    <div className="modal-actions">
                        <button type="button" className="btn-modal-cancel" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-modal-submit" disabled={loading}>
                            {loading ? "Saving..." : "Save changes"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
